import { useCallback, useState } from 'react'

/**
 * Like useState, but persists the value to localStorage under the given key.
 * Falls back to defaultValue if localStorage is unavailable (SSR, private
 * browsing, quota exceeded) or the stored item cannot be parsed.
 *
 * Supports functional updaters: setValue(prev => newValue)
 */
function usePersistedState<T>(
  key: string,
  defaultValue: T
): [T, (value: T | ((prev: T) => T)) => void] {
  const [state, setState] = useState<T>(() => {
    if (globalThis.window === undefined) return defaultValue
    try {
      const item = globalThis.window.localStorage.getItem(key)
      if (item === null) return defaultValue
      // Try JSON first; fall back to the raw string for legacy plain-string values
      try {
        return JSON.parse(item) as T
      } catch {
        return item as unknown as T
      }
    } catch {
      return defaultValue
    }
  })

  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      setState((prev) => {
        const next =
          typeof value === 'function' ? (value as (prev: T) => T)(prev) : value
        try {
          globalThis.window.localStorage.setItem(
            key,
            typeof next === 'string' ? next : JSON.stringify(next)
          )
        } catch {
          // Ignore write errors (private browsing, storage quota, etc.)
        }
        return next
      })
    },
    [key]
  )

  return [state, setValue]
}

export default usePersistedState
